/**
 * m² de muro de un plano 2D con sus vanos descontados (paso 6 del plan de medición DWG).
 *
 *   bruto = eje bruto × alto          (el eje bruto ya incluye el largo de los vanos)
 *   neto  = bruto − Σ (ancho × alto) de cada vano
 *
 * El lector (`plano2svg.py`) da el eje bruto y los vanos con su ancho y su bloque; el ALTO de cada vano
 * no está en una planta: lo pone quien cotiza, una vez por tipo de bloque (o sale de otra lámina). Un
 * vano sin alto NO se descuenta en silencio: el neto queda null y se dice cuántos faltan.
 * Función pura: la usan la pantalla y la vara.
 */
export interface VanoParaM2 {
  /** Ancho del vano en metros. */
  anchoM: number;
  /** Alto del vano en metros; null = todavía no se sabe. */
  altoM: number | null;
}

export interface M2DeMuro {
  brutoM2: number;
  /** null si algún vano no tiene alto. */
  netoM2: number | null;
  vanosM2: number;
  vanosSinAlto: number;
}

export function m2DeMuroConVanos(ejeBrutoM: number, altoM: number, vanos: readonly VanoParaM2[]): M2DeMuro {
  const brutoM2 = ejeBrutoM * altoM;
  let vanosM2 = 0;
  let vanosSinAlto = 0;
  for (const v of vanos) {
    if (v.altoM == null || !Number.isFinite(v.altoM)) vanosSinAlto += 1;
    else vanosM2 += v.anchoM * v.altoM;
  }
  return { brutoM2, netoM2: vanosSinAlto ? null : brutoM2 - vanosM2, vanosM2, vanosSinAlto };
}

// ---------------------------------------------------------------------------
// Vanos agrupados por TIPO (bloque asociado + ancho) y la cuenta del muro con sus descuentos.
// El alto se captura una vez por tipo y vale para todos los vanos de ese tipo.
// ---------------------------------------------------------------------------

/** Un vano tal como lo da el inventario del plano (unidades del dibujo). */
export interface VanoDelPlano {
  ancho: number;
  bloque: string | null;
  /** Sin bloque, pero con trazos de puerta o ventana dibujados con líneas (solo afecta el nombre). */
  dibujado?: boolean;
}

/** Un tipo de vano: mismos bloque y ancho (redondeado al centímetro). */
export interface TipoDeVano {
  /** Identidad estable del tipo dentro del plano: con ella se guarda su alto. */
  clave: string;
  bloque: string | null;
  anchoM: number;
  n: number;
  /**
   * Solo para el NOMBRE (no entra en `clave`, para no perder el alto guardado): true si el tipo no tiene
   * bloque y TODOS sus vanos vienen de trazos dibujados. Si el mismo bloque-ancho mezcla vanos dibujados y
   * huecos sin evidencia, queda false (se nombra «hueco sin puerta ni ventana», lo conservador).
   */
  dibujado?: boolean;
}

const alCentimetro = (m: number): number => Math.round(m * 100) / 100;

/** Clave de un tipo de vano: bloque + ancho en metros al centímetro ("puerta 0.90|0.85"). */
export function claveDeTipoDeVano(bloque: string | null, anchoM: number): string {
  return `${bloque ?? ''}|${alCentimetro(anchoM).toFixed(2)}`;
}

/**
 * Agrupa los vanos por tipo. `factor` = unidad del dibujo → metro. Orden: con bloque primero (por
 * nombre), luego por ancho; así la lista no brinca entre aperturas del plano.
 */
export function agruparVanosPorTipo(vanos: readonly VanoDelPlano[], factor: number): TipoDeVano[] {
  const porClave = new Map<string, TipoDeVano & { _dib: number }>();
  for (const v of vanos) {
    if (!Number.isFinite(v.ancho) || v.ancho <= 0) continue;
    const anchoM = alCentimetro(v.ancho * factor);
    const clave = claveDeTipoDeVano(v.bloque, anchoM);
    const t = porClave.get(clave);
    const dib = !v.bloque && v.dibujado === true ? 1 : 0;
    if (t) {
      t.n += 1;
      t._dib += dib;
    } else porClave.set(clave, { clave, bloque: v.bloque, anchoM, n: 1, _dib: dib });
  }
  return Array.from(porClave.values()).map(({ _dib, ...t }) => (_dib > 0 && _dib === t.n ? { ...t, dibujado: true } : t)).sort((a, b) => {
    if ((a.bloque == null) !== (b.bloque == null)) return a.bloque == null ? 1 : -1;
    const nb = (a.bloque ?? '').localeCompare(b.bloque ?? '', 'es-MX');
    return nb !== 0 ? nb : a.anchoM - b.anchoM;
  });
}

/** Un tramo de muro (una planta, o el plano entero si no tiene vistas), ya en metros. */
export interface TramoDeMuro {
  ejeBrutoM: number;
  altoM: number;
  vanos: readonly VanoDelPlano[];
}

export interface CuentaDeMuroConVanos {
  /** Σ eje bruto × alto de cada tramo. */
  brutoM2: number;
  /** m² descontados por tipo (solo los tipos con alto). */
  descuentos: { clave: string; bloque: string | null; anchoM: number; n: number; m2: number; dibujado?: boolean }[];
  /** null mientras falte el alto de algún tipo. */
  netoM2: number | null;
  /** Cuántos TIPOS no tienen alto todavía. */
  tiposSinAlto: number;
  /**
   * La misma cuenta tramo por tramo (mismo orden que la entrada), para mostrar cada planta con SU
   * bruto, sus vanos y su neto: así el desglose por planta suma exactamente lo que dice el total.
   */
  porTramo: { brutoM2: number; vanosM2: number; netoM2: number | null }[];
}

/**
 * La cuenta completa: bruto − Σ(ancho × alto) de cada vano, con el alto de su tipo. Usa
 * `m2DeMuroConVanos` tramo por tramo (cada planta con su alto de muro).
 */
export function cuentaDeMuroConVanos(
  tramos: readonly TramoDeMuro[],
  factor: number,
  altosPorTipo: Readonly<Record<string, number | null | undefined>>,
): CuentaDeMuroConVanos {
  let brutoM2 = 0;
  let netoM2: number | null = 0;
  const descuentos = new Map<string, CuentaDeMuroConVanos['descuentos'][number]>();
  const sinAlto = new Set<string>();
  const porTramo: CuentaDeMuroConVanos['porTramo'] = [];
  for (const t of tramos) {
    const vanos: VanoParaM2[] = [];
    for (const v of t.vanos) {
      if (!Number.isFinite(v.ancho) || v.ancho <= 0) continue;
      const anchoM = alCentimetro(v.ancho * factor);
      const clave = claveDeTipoDeVano(v.bloque, anchoM);
      const a = altosPorTipo[clave];
      const altoM = a != null && Number.isFinite(a) && a > 0 ? a : null;
      vanos.push({ anchoM, altoM });
      if (altoM == null) {
        sinAlto.add(clave);
        continue;
      }
      const d = descuentos.get(clave) ?? { clave, bloque: v.bloque, anchoM, n: 0, m2: 0 };
      const dib = !v.bloque && v.dibujado === true;
      d.dibujado = d.n === 0 ? dib || undefined : d.dibujado && dib ? true : undefined;
      d.n += 1;
      d.m2 += anchoM * altoM;
      descuentos.set(clave, d);
    }
    const r = m2DeMuroConVanos(t.ejeBrutoM, t.altoM, vanos);
    brutoM2 += r.brutoM2;
    netoM2 = netoM2 == null || r.netoM2 == null ? null : netoM2 + r.netoM2;
    porTramo.push({ brutoM2: r.brutoM2, vanosM2: r.vanosM2, netoM2: r.netoM2 });
  }
  return { brutoM2, descuentos: Array.from(descuentos.values()), netoM2, tiposSinAlto: sinAlto.size, porTramo };
}

/**
 * Propone el alto de un tipo de vano DESDE EL PROPIO PLANO, sin IA y sin valores típicos: busca en los
 * textos y en los atributos del bloque una medida escrita "ancho × alto" (p. ej. "0.90x2.10",
 * "90 X 210") cuyo primer número sea el ancho del vano en metros, en centímetros o en la unidad del
 * dibujo. El segundo número, en esa misma unidad, es el alto. Si el plano no lo dice, o se contradice,
 * devuelve null (el usuario lo escribe).
 */
export function proponerAltoDeVano(
  tipo: Pick<TipoDeVano, 'bloque' | 'anchoM'>,
  anotaciones: { textos?: readonly string[]; atributos?: readonly { bloque: string; tag: string; valor: string }[] } | null | undefined,
  factor: number,
): number | null {
  if (!anotaciones) return null;
  const fuentes = [
    ...(anotaciones.textos ?? []),
    ...(tipo.bloque ? (anotaciones.atributos ?? []).filter((a) => a.bloque === tipo.bloque).map((a) => a.valor) : []),
  ];
  // Unidades posibles del texto: metros, centímetros y la del dibujo (sin repetir).
  const escalas = Array.from(new Set([1, 0.01, factor].filter((f) => Number.isFinite(f) && f > 0)));
  const hallados = new Set<number>();
  const re = /(\d+(?:[.,]\d+)?)\s*[xX×]\s*(\d+(?:[.,]\d+)?)/g;
  for (const s of fuentes) {
    for (const m of s.matchAll(re)) {
      const a = Number(m[1]!.replace(',', '.'));
      const h = Number(m[2]!.replace(',', '.'));
      for (const f of escalas) {
        if (Math.abs(a * f - tipo.anchoM) <= 0.005 && h > 0) hallados.add(alCentimetro(h * f));
      }
    }
  }
  return hallados.size === 1 ? Array.from(hallados)[0]! : null;
}
